import {
    CalendarOutlined,
    FileExcelOutlined,
    PrinterOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    message,
    Modal,
    Row,
    Spin,
    Statistic,
    Table,
    Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../contexts/ApiContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import EInvoiceStatusChip from '../components/eInvoice/EInvoiceStatusChip';
import { useEInvoiceStatuses } from '../hooks/useEInvoiceStatuses';
import { useInvoicePreview } from '../hooks/useInvoicePreview';

const { Title, Text } = Typography;

interface ReportTransaction {
    id: number;
    reportDate: string;
    transactionTime: string;
    contractNumber: string;
    clientName: string;
    itemDescription: string;
    quantity: string;
    amountEur: number;
    exchangeRate: number;
    amountLei: number;
    dailyInterestRate: number;
    pricePerGram: number;
}

interface Invoice {
    id: number;
    invoiceDate: string;
    invoiceTime: string;
    contractNumber: string;
    clientName: string;
    invoiceNumber: string;
    amountEur: number;
    exchangeRate: number;
    amountLei: number;
    invoiceType: string;
}

interface DailyReportSummary {
    outputsEur: number;
    outputsLei: number;
    inputsEur: number;
    inputsLei: number;
    contractsCount: number;
    paidInvoicesCount: number;
    extensionInvoicesCount: number;
    saleInvoicesCount: number;
    totalOperations: number;
    saleInvoicesLei: number;
    saleInvoicesStornoLei: number;
    saleInvoicesBaseLei: number;
    saleInvoicesTvaLei: number;
    vatRate: number;
}

interface DailyReportData {
    outputs: ReportTransaction[];
    inputs: Invoice[];
    /** The cash session the report is narrowed to; `null` for the whole day. */
    sessionNumber?: number | null;
    summary: DailyReportSummary;
}

const Reports = () => {
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
    const [sessionFilter, setSessionFilter] = useState(false);
    const [reportData, setReportData] = useState<DailyReportData | null>(null);
    const { t } = useTranslation();
    const api = useApi();
    const { cashPointId } = useCashDrawer();
    const { available: eInvoiceAvailable, rowsByInvoice } = useEInvoiceStatuses(
        reportData?.inputs?.map(invoice => invoice.id) ?? [],
    );

    const {
        invoicePreview,
        invoicePreviewLoading,
        invoicePreviewError,
        showInvoicePreview: showInvoicePreviewRaw,
        closeInvoicePreview,
    } = useInvoicePreview(t);

    const showInvoicePreview = useCallback(
        async (invoice: Invoice) => {
            const invoiceNumber = invoice.invoiceNumber.trim();
            if (!invoiceNumber) {
                message.error(t('reports.invoicePreviewError'));
                return;
            }
            await showInvoicePreviewRaw(invoiceNumber, 'reports.invoicePreviewError');
        },
        [showInvoicePreviewRaw, t],
    );

    const createBaseReportColumns = (fields: {
        dateField: string;
        timeField: string;
        clientWidth?: number;
    }) => [
        {
            title: t('reports.date'),
            dataIndex: fields.dateField,
            key: fields.dateField,
            width: 100,
            render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
        },
        {
            title: t('reports.time'),
            dataIndex: fields.timeField,
            key: fields.timeField,
            width: 80,
        },
        {
            title: t('reports.contractNumber'),
            dataIndex: 'contractNumber',
            key: 'contractNumber',
            width: 130,
        },
        {
            title: t('reports.clientName'),
            dataIndex: 'clientName',
            key: 'clientName',
            width: fields.clientWidth ?? 150,
            ellipsis: true,
        },
    ];

    const loadDailyReport = useCallback(async () => {
        // The current session is the one open at this workstation's register, so the
        // register has to be known before the report can be narrowed to it.
        if (sessionFilter && cashPointId === null) {
            message.error(t('reports.noCashPoint'));
            setReportData(null);
            return;
        }
        try {
            setLoading(true);
            const dateStr = selectedDate.format('YYYY-MM-DD');

            const result = await api.reports.getDailyReport({
                date: dateStr,
                sessionFilter: sessionFilter,
                cashPointId,
            });

            if (result.success && result.data) {
                setReportData(result.data as unknown as DailyReportData);
            } else {
                message.error(result.error ?? t('common.error'));
                setReportData(null);
            }
        } catch (error) {
            console.error('Load daily report error:', error);
            message.error(t('common.error'));
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, sessionFilter, cashPointId, api.reports, t]);

    useEffect(() => {
        loadDailyReport();
    }, [loadDailyReport]);

    const handleDateChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    const handleSessionFilterChange = (checked: boolean) => {
        setSessionFilter(checked);
    };

    const handleExportExcel = async () => {
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const result = await api.reports.exportDailyReport({
                date: dateStr,
                sessionFilter: sessionFilter,
                cashPointId,
                format: 'excel',
            });

            if (result.success && result.data) {
                // result.data is already a Blob from the API
                const url = URL.createObjectURL(result.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `raport_zilnic_${dateStr}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
                message.success(t('common.exportSuccess'));
            } else {
                message.error(result.error ?? t('common.error'));
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error(t('common.error'));
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Output (Iesiri) table columns - contracts created on this date
    const outputColumns: ColumnsType<ReportTransaction> = [
        ...createBaseReportColumns({ dateField: 'reportDate', timeField: 'transactionTime' }),
        {
            title: t('reports.object'),
            dataIndex: 'itemDescription',
            key: 'itemDescription',
            width: 120,
            ellipsis: true,
        },
        {
            title: t('reports.quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
            align: 'right',
        },
        {
            title: t('reports.amountEur'),
            dataIndex: 'amountEur',
            key: 'amountEur',
            width: 100,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(2),
        },
        {
            title: t('reports.exchangeRate'),
            dataIndex: 'exchangeRate',
            key: 'exchangeRate',
            width: 80,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(4),
        },
        {
            title: t('reports.amountLei'),
            dataIndex: 'amountLei',
            key: 'amountLei',
            width: 100,
            align: 'right',
            render: (value: number) => Math.round(value ?? 0).toLocaleString(),
        },
        {
            title: t('reports.commission'),
            dataIndex: 'dailyInterestRate',
            key: 'dailyInterestRate',
            width: 90,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(2),
        },
        {
            title: t('reports.pricePerGram'),
            dataIndex: 'pricePerGram',
            key: 'pricePerGram',
            width: 90,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(2),
        },
    ];

    // Input (Intrari) table columns - invoices created on this date
    const inputColumns: ColumnsType<Invoice> = [
        ...createBaseReportColumns({
            dateField: 'invoiceDate',
            timeField: 'invoiceTime',
            clientWidth: 180,
        }),
        {
            title: t('reports.invoiceNumber'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            width: 100,
        },
        {
            title: t('reports.amountEur'),
            dataIndex: 'amountEur',
            key: 'amountEur',
            width: 100,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(2),
        },
        {
            title: t('reports.exchangeRate'),
            dataIndex: 'exchangeRate',
            key: 'exchangeRate',
            width: 80,
            align: 'right',
            render: (value: number | string) => Number(value ?? 0).toFixed(2),
        },
        {
            title: t('reports.amountLei'),
            dataIndex: 'amountLei',
            key: 'amountLei',
            width: 100,
            align: 'right',
            render: (value: number) => Math.round(value ?? 0).toLocaleString(),
        },
        {
            title: t('reports.invoiceType'),
            dataIndex: 'invoiceType',
            key: 'invoiceType',
            width: 120,
            render: (value: string) => {
                if (value?.includes('Achitare')) return t('reports.paymentType');
                if (value?.includes('Prelungire')) return t('reports.extensionType');
                if (value?.includes('V.Marfa')) return t('reports.saleType');
                return value ?? '';
            },
        },
        ...(eInvoiceAvailable
            ? [
                  {
                      title: t('eInvoice.chip.column'),
                      key: 'eInvoice',
                      width: 120,
                      render: (_value: unknown, invoice: Invoice) => (
                          <EInvoiceStatusChip rows={rowsByInvoice.get(invoice.id)} />
                      ),
                  },
              ]
            : []),
    ];

    const summary = reportData?.summary ?? {
        outputsEur: 0,
        outputsLei: 0,
        inputsEur: 0,
        inputsLei: 0,
        contractsCount: 0,
        paidInvoicesCount: 0,
        extensionInvoicesCount: 0,
        saleInvoicesCount: 0,
        totalOperations: 0,
        saleInvoicesLei: 0,
        saleInvoicesStornoLei: 0,
        saleInvoicesBaseLei: 0,
        saleInvoicesTvaLei: 0,
        vatRate: 0.19,
    };

    const balanceEur = summary.inputsEur - summary.outputsEur;
    const balanceLei = summary.inputsLei - summary.outputsLei;

    const renderCurrencySummaryCard = (config: {
        background: string;
        borderColor: string;
        currencyLabel: string;
        inputLabel: string;
        outputLabel: string;
        balanceLabel: string;
        inputValue: string;
        outputValue: string;
        balanceValue: string;
        balance: number;
    }) => (
        <Card
            size="small"
            style={{
                background: config.background,
                borderColor: config.borderColor,
            }}
        >
            <Text strong>{config.currencyLabel}</Text>
            <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">{config.inputLabel}:</Text>
                    <Text strong style={{ float: 'right', color: '#389e0d' }}>
                        {config.inputValue}
                    </Text>
                </div>
                <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">{config.outputLabel}:</Text>
                    <Text strong style={{ float: 'right', color: '#cf1322' }}>
                        {config.outputValue}
                    </Text>
                </div>
                <div
                    style={{
                        borderTop: '1px solid #d9d9d9',
                        paddingTop: 4,
                        marginTop: 4,
                    }}
                >
                    <Text strong>{config.balanceLabel}:</Text>
                    <Text
                        strong
                        style={{
                            float: 'right',
                            color: config.balance >= 0 ? '#389e0d' : '#cf1322',
                        }}
                    >
                        {config.balanceValue}
                    </Text>
                </div>
            </div>
        </Card>
    );

    let invoicePreviewContent = (
        <Text type="danger">{invoicePreviewError ?? t('reports.invoicePreviewError')}</Text>
    );

    if (invoicePreviewLoading) {
        invoicePreviewContent = (
            <div
                aria-label={t('reports.invoicePreviewLoading')}
                style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}
            >
                <Spin size="large" />
            </div>
        );
    } else if (invoicePreview?.url) {
        invoicePreviewContent = (
            <iframe
                title={t('reports.invoicePreviewTitle', {
                    invoiceNumber: invoicePreview.invoiceNumber,
                })}
                src={invoicePreview.url}
                style={{ border: 0, height: '100%', width: '100%' }}
            />
        );
    }

    return (
        <div className="daily-report-page">
            {/* Page Header */}
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#ea580c' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <CalendarOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('reports.title')}
                        </Title>
                        <Text type="secondary">{t('reports.subtitle')}</Text>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8} md={6} lg={5}>
                        <Text strong>{t('reports.selectDate')}:</Text>
                        <DatePicker
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            style={{ width: '100%', marginTop: 8 }}
                            allowClear={false}
                            // The session filter covers the whole session, whatever its days.
                            disabled={sessionFilter}
                        />
                    </Col>
                    <Col xs={24} sm={8} md={6} lg={4}>
                        <div style={{ marginTop: 28 }}>
                            <Checkbox
                                checked={sessionFilter}
                                onChange={e => handleSessionFilterChange(e.target.checked)}
                            >
                                {t('reports.sessionFilter')}
                            </Checkbox>
                            {sessionFilter && reportData?.sessionNumber != null && (
                                <div>
                                    <Text type="secondary" data-testid="daily-report-session">
                                        {t('reports.sessionNumber', {
                                            number: reportData.sessionNumber,
                                        })}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col xs={24} sm={8} md={12} lg={15}>
                        <div style={{ marginTop: 28, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={loadDailyReport}
                                loading={loading}
                            >
                                {t('reports.calculate')}
                            </Button>
                            <Button
                                icon={<FileExcelOutlined />}
                                onClick={handleExportExcel}
                                disabled={!reportData}
                            >
                                {t('reports.exportExcel')}
                            </Button>
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={handlePrint}
                                disabled={!reportData}
                            >
                                {t('reports.print')}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    {/* Summary Section */}
                    <Row
                        gutter={[24, 24]}
                        style={{ marginBottom: 24, maxWidth: 1200 }}
                        align="stretch"
                    >
                        {/* Financial Summary */}
                        <Col xs={24} lg={12} style={{ display: 'flex' }}>
                            <Card title={t('reports.reportSummary')} style={{ flex: 1 }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        {renderCurrencySummaryCard({
                                            background: '#f6ffed',
                                            borderColor: '#b7eb8f',
                                            currencyLabel: t('reports.euro'),
                                            inputLabel: t('reports.totalInputsEur'),
                                            outputLabel: t('reports.totalOutputsEur'),
                                            balanceLabel: t('reports.balanceEur'),
                                            inputValue: Number(summary.inputsEur ?? 0).toFixed(2),
                                            outputValue: Number(summary.outputsEur ?? 0).toFixed(2),
                                            balanceValue: Number(balanceEur ?? 0).toFixed(2),
                                            balance: balanceEur,
                                        })}
                                    </Col>
                                    <Col span={12}>
                                        {renderCurrencySummaryCard({
                                            background: '#e6f7ff',
                                            borderColor: '#91caff',
                                            currencyLabel: t('reports.lei'),
                                            inputLabel: t('reports.totalInputsLei'),
                                            outputLabel: t('reports.totalOutputsLei'),
                                            balanceLabel: t('reports.balanceLei'),
                                            inputValue: Math.round(
                                                summary.inputsLei ?? 0,
                                            ).toLocaleString(),
                                            outputValue: Math.round(
                                                summary.outputsLei ?? 0,
                                            ).toLocaleString(),
                                            balanceValue: Math.round(
                                                balanceLei ?? 0,
                                            ).toLocaleString(),
                                            balance: balanceLei,
                                        })}
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        {/* Operations Count Summary */}
                        <Col xs={24} lg={12} style={{ display: 'flex' }}>
                            <Card title={t('reports.summary')} style={{ flex: 1 }}>
                                <Row gutter={[8, 0]}>
                                    <Col span={6}>
                                        <Statistic
                                            title={t('reports.contractsCount')}
                                            value={summary.contractsCount ?? 0}
                                            valueStyle={{ color: '#cf1322', fontSize: 20 }}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Statistic
                                            title={t('reports.paidInvoices')}
                                            value={summary.paidInvoicesCount ?? 0}
                                            valueStyle={{ color: '#389e0d', fontSize: 20 }}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Statistic
                                            title={t('reports.extensionInvoices')}
                                            value={summary.extensionInvoicesCount ?? 0}
                                            valueStyle={{ color: '#1890ff', fontSize: 20 }}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Statistic
                                            title={t('reports.saleInvoices')}
                                            value={summary.saleInvoicesCount ?? 0}
                                            valueStyle={{ color: '#722ed1', fontSize: 20 }}
                                        />
                                    </Col>
                                </Row>
                                {summary.saleInvoicesCount > 0 && (
                                    <Row gutter={16} style={{ marginTop: 12 }}>
                                        <Col span={12}>
                                            <Statistic
                                                title={t('reports.saleType')}
                                                value={Math.round(summary.saleInvoicesLei ?? 0)}
                                                suffix="LEI"
                                                valueStyle={{ color: '#722ed1', fontSize: 16 }}
                                            />
                                        </Col>
                                        {(summary.saleInvoicesStornoLei ?? 0) > 0 && (
                                            <Col span={12}>
                                                <Statistic
                                                    title={t('reports.creditedStorno')}
                                                    value={Math.round(
                                                        summary.saleInvoicesStornoLei ?? 0,
                                                    )}
                                                    suffix="LEI"
                                                    valueStyle={{ color: '#cf1322', fontSize: 16 }}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                )}
                                <div
                                    style={{
                                        borderTop: '2px solid #d9d9d9',
                                        paddingTop: 16,
                                        marginTop: 14,
                                        paddingBottom: 8,
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 8,
                                    }}
                                >
                                    <Text type="secondary">{t('reports.totalOperations')}:</Text>
                                    <Text strong style={{ fontSize: 20 }}>
                                        {summary.totalOperations ?? 0}
                                    </Text>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Outputs Grid (Iesiri) - Contracts */}
                    <Card
                        title={
                            <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                                {t('reports.outputs')} ({reportData?.outputs?.length ?? 0})
                            </span>
                        }
                        style={{ marginBottom: 24 }}
                        styles={{ body: { padding: 0 } }}
                    >
                        <Table
                            columns={outputColumns}
                            dataSource={reportData?.outputs ?? []}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 1200, y: 300 }}
                            size="small"
                            locale={{ emptyText: t('reports.noData') }}
                        />
                    </Card>

                    {/* Inputs Grid (Intrari) - Invoices */}
                    <Card
                        title={
                            <span style={{ color: '#389e0d', fontWeight: 'bold' }}>
                                {t('reports.inputs')} ({reportData?.inputs?.length ?? 0})
                            </span>
                        }
                        style={{ marginBottom: 24 }}
                        styles={{ body: { padding: 0 } }}
                    >
                        <Table
                            columns={inputColumns}
                            dataSource={reportData?.inputs ?? []}
                            rowKey="id"
                            onRow={invoice => ({
                                'data-invoice-number': invoice.invoiceNumber,
                                onContextMenu: event => {
                                    event.preventDefault();
                                    void showInvoicePreview(invoice);
                                },
                            })}
                            pagination={false}
                            scroll={{ x: 1000, y: 300 }}
                            size="small"
                            locale={{ emptyText: t('reports.noData') }}
                        />
                    </Card>
                </>
            )}

            <Modal
                title={
                    invoicePreview
                        ? t('reports.invoicePreviewTitle', {
                              invoiceNumber: invoicePreview.invoiceNumber,
                          })
                        : t('reports.invoicePreviewTitle', { invoiceNumber: '' })
                }
                open={invoicePreview !== null}
                onCancel={closeInvoicePreview}
                footer={null}
                width="80%"
                styles={{ body: { height: '75vh' } }}
            >
                {invoicePreviewContent}
            </Modal>

            {/* Print-specific styles */}
            <style>{`
                @media print {
                    .ant-layout-sider,
                    .ant-layout-header,
                    .ant-btn,
                    .ant-picker,
                    .ant-checkbox-wrapper {
                        display: none !important;
                    }
                    .daily-report-page {
                        padding: 0 !important;
                    }
                    .ant-card {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                }
            `}</style>
        </div>
    );
};

export default Reports;
